import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const workflowSteps = [
  {
    id: 1,
    title: "Project Initialization",
    desc: "Define scope, set target budget, and choose category.",
    icon: "PlusCircle",
    color: "text-maker-cyan",
    details: "Makers start by documenting the 'Why' and 'How much'. This phase creates the blueprint for resource allocation."
  },
  {
    id: 2,
    title: "Material Bin Check",
    desc: "Auto-scan existing inventory for required parts.",
    icon: "Box",
    color: "text-maker-tan",
    details: "CraftCloud links your project requirements to your physical bins (A-12, B-04, etc.) to prevent duplicate buying."
  },
  {
    id: 3,
    title: "Pattern & Notes",
    desc: "Upload sketches or digital patterns for reference.",
    icon: "FileText",
    color: "text-blue-400",
    details: "Store all PDF patterns, 3D print settings, and hand-written notes in one centralized digital workshop."
  },
  {
    id: 4,
    title: "Cost Estimation",
    desc: "Real-time calculation of material burn rate.",
    icon: "DollarSign",
    color: "text-green-400",
    details: "See precisely how much of your walnut slab or PLA filament is consumed, including waste margins."
  },
  {
    id: 5,
    title: "Production Log",
    desc: "Timed photo log of the build process.",
    icon: "Camera",
    color: "text-purple-400",
    details: "Essential for small sellers documenting their 'handmade' process for Etsy or Portfolio displays."
  },
  {
    id: 6,
    title: "Deadline Oversight",
    desc: "Gantt-style tracking for creative orders.",
    icon: "Clock",
    color: "text-red-400",
    details: "Never miss a commission deadline. The system alerts you when material lead times threaten delivery."
  }
];

const sampleData = [
  { id: 'S-101', name: 'Custom Mechanical Keyboard', type: 'Electronics', stock: 'Low', value: '$240', status: 'In Progress' },
  { id: 'S-102', name: 'Leather Satchel V1', type: 'Leatherwork', stock: 'Full', value: '$85', status: 'Draft' },
  { id: 'S-103', name: 'CNC Carved Sign', type: 'Woodworking', stock: 'Out', value: '$12', status: 'Blocked' },
  { id: 'S-104', name: 'Resin Dice Set #4', type: 'Casting', stock: 'Full', value: '$110', status: 'Finishing' },
];

export default function DemoView() {
  const [activeStep, setActiveStep] = useState(1);
  const [selectedState, setSelectedState] = useState('success');
  const [filterType, setFilterType] = useState('All');

  const filteredData = filterType === 'All' 
    ? sampleData 
    : sampleData.filter(d => d.type === filterType);

  const renderSimulatedState = () => {
    switch(selectedState) {
      case 'loading':
        return (
          <div className="flex flex-col items-center justify-center p-12 border border-graphite-700 bg-graphite-800/50 rounded-xl">
            <div className="w-10 h-10 border-4 border-maker-cyan border-t-transparent rounded-full animate-spin mb-4" />
            <p className="font-mono text-maker-cyan animate-pulse">FETCHING_DATABASE...</p>
          </div>
        );
      case 'empty':
        return (
          <div className="flex flex-col items-center justify-center p-12 border border-dashed border-graphite-600 rounded-xl text-gray-500">
            <SafeIcon name="Inbox" className="w-12 h-12 mb-2 opacity-20" />
            <p className="font-mono text-sm">NO_RECORDS_FOUND</p>
          </div>
        );
      case 'error':
        return (
          <div className="flex flex-col items-center justify-center p-12 border border-red-500/30 bg-red-500/5 rounded-xl text-red-400">
            <SafeIcon name="AlertTriangle" className="w-12 h-12 mb-2" />
            <p className="font-mono font-bold">CONNECTION_FAILURE_404</p>
            <p className="text-xs mt-1 text-red-400/60 uppercase">Check workshop uplink</p>
          </div>
        );
      default:
        return (
          <div className="flex flex-col items-center justify-center p-12 border border-maker-cyan/30 bg-maker-cyan/5 rounded-xl text-maker-cyan glow-cyan">
            <SafeIcon name="CheckCircle" className="w-12 h-12 mb-2" />
            <p className="font-mono font-bold">SYSTEM_SUCCESS</p>
            <p className="text-xs mt-1 text-maker-cyan/60 uppercase">Operation verified</p>
          </div>
        );
    }
  };

  return (
    <div className="space-y-12 pb-20">
      <header>
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Demo / How It Works</h1>
        <p className="text-gray-400 font-mono text-sm">USER_JOURNEY // INTERACTIVE_SIMULATION</p>
      </header>

      {/* Section: Try this flow */}
      <section className="space-y-6">
        <div className="flex items-center gap-2 text-maker-tan">
          <SafeIcon name="Play" className="w-5 h-5" />
          <h2 className="text-xl font-bold uppercase tracking-widest">Try this flow</h2>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1 space-y-3">
            {workflowSteps.map((step) => (
              <button
                key={step.id}
                onClick={() => setActiveStep(step.id)}
                className={`w-full text-left p-4 rounded-xl border transition-all duration-300 flex items-center gap-4 ${
                  activeStep === step.id 
                    ? 'bg-maker-cyan/10 border-maker-cyan text-white glow-cyan' 
                    : 'bg-graphite-800 border-graphite-700 text-gray-400 hover:border-graphite-600'
                }`}
              >
                <div className={`p-2 rounded-lg bg-graphite-900 ${activeStep === step.id ? step.color : 'text-gray-600'}`}>
                  <SafeIcon name={step.icon} className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-bold uppercase">{step.title}</p>
                  <p className="text-xs opacity-60 font-mono">STEP_0{step.id}</p>
                </div>
              </button>
            ))}
          </div>

          <div className="lg:col-span-2 glass-panel rounded-2xl p-8 relative overflow-hidden flex flex-col justify-center">
            <div className="absolute top-0 right-0 p-8 opacity-5">
               <SafeIcon name={workflowSteps[activeStep - 1].icon} className="w-64 h-64" />
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="relative z-10"
              >
                <div className={`inline-block px-3 py-1 rounded text-[10px] font-mono border mb-4 ${workflowSteps[activeStep-1].color} border-current bg-current/5`}>
                  ACTIVE_MODULE: {workflowSteps[activeStep-1].title.toUpperCase()}
                </div>
                <h3 className="text-3xl font-bold text-white mb-4">{workflowSteps[activeStep-1].title}</h3>
                <p className="text-xl text-gray-300 mb-6 leading-relaxed">{workflowSteps[activeStep - 1].desc}</p>
                <div className="p-4 bg-graphite-900/50 border-l-4 border-maker-cyan rounded-r-lg">
                  <p className="text-gray-400 italic text-sm">"{workflowSteps[activeStep - 1].details}"</p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* Section: Sample Data */}
      <section className="space-y-6">
        <div className="flex items-center gap-2 text-maker-cyan">
          <SafeIcon name="Database" className="w-5 h-5" />
          <h2 className="text-xl font-bold uppercase tracking-widest">Sample Data</h2>
        </div>

        <div className="glass-panel rounded-xl overflow-hidden">
          <div className="p-4 bg-graphite-800/50 border-b border-graphite-700 flex flex-col md:flex-row justify-between gap-4">
            <div className="flex gap-2">
              {['All', 'Electronics', 'Woodworking', 'Leatherwork'].map(t => (
                <button 
                  key={t}
                  onClick={() => setFilterType(t)}
                  className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                    filterType === t 
                      ? 'bg-maker-cyan text-graphite-900 border-maker-cyan font-bold' 
                      : 'border-graphite-600 text-gray-400 hover:border-gray-400'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
            <span className="text-xs font-mono text-gray-500 self-center">RECORDS: {filteredData.length}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-graphite-700 text-gray-500 text-[10px] uppercase font-mono tracking-tighter">
                  <th className="px-6 py-4">ID</th>
                  <th className="px-6 py-4">Designation</th>
                  <th className="px-6 py-4">Category</th>
                  <th className="px-6 py-4">Stock</th>
                  <th className="px-6 py-4">Value</th>
                  <th className="px-6 py-4 text-right">Status</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {filteredData.map((row) => (
                  <tr key={row.id} className="border-b border-graphite-700/50 hover:bg-graphite-800/30 transition-colors">
                    <td className="px-6 py-4 font-mono text-xs text-maker-cyan">{row.id}</td>
                    <td className="px-6 py-4 text-white font-bold">{row.name}</td>
                    <td className="px-6 py-4 text-gray-400">{row.type}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-0.5 rounded text-[10px] border ${
                        row.stock === 'Full' ? 'border-green-500/50 text-green-400' : 
                        row.stock === 'Low' ? 'border-maker-tan/50 text-maker-tan' : 'border-red-500/50 text-red-500'
                      }`}>
                        {row.stock}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-mono text-maker-tan">{row.value}</td>
                    <td className="px-6 py-4 text-right">
                      <span className="text-xs text-gray-500">{row.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Section: Simulated States */}
      <section className="space-y-6">
        <div className="flex items-center gap-2 text-red-400">
          <SafeIcon name="Shield" className="w-5 h-5" />
          <h2 className="text-xl font-bold uppercase tracking-widest">Simulated States</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-1 space-y-2">
            {['success', 'loading', 'empty', 'error'].map(s => (
              <button
                key={s}
                onClick={() => setSelectedState(s)}
                className={`w-full p-3 rounded-lg border text-sm font-mono uppercase transition-all ${
                  selectedState === s 
                    ? 'bg-graphite-700 border-gray-400 text-white' 
                    : 'border-graphite-700 text-gray-500 hover:text-gray-300'
                }`}
              >
                Trigger: {s}
              </button>
            ))}
          </div>
          <div className="md:col-span-3">
             <AnimatePresence mode="wait">
               <motion.div
                 key={selectedState}
                 initial={{ opacity: 0, scale: 0.98 }}
                 animate={{ opacity: 1, scale: 1 }}
                 exit={{ opacity: 0, scale: 0.98 }}
                 transition={{ duration: 0.2 }}
               >
                 {renderSimulatedState()}
               </motion.div>
             </AnimatePresence>
          </div>
        </div>
      </section>

      <footer className="pt-12 border-t border-graphite-800 text-center">
        <p className="text-gray-600 font-mono text-xs tracking-widest uppercase">
          Made by LingLink AI and DigLab AI
        </p>
      </footer>
    </div>
  );
}